import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Check, Building2, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$9.99',
    period: '/mo',
    description: 'Perfect for small teams',
    features: ['Up to 10 workers', 'GPS time tracking', 'Job management', 'Basic reports', '14-day free trial']
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$29.99',
    period: '/mo',
    description: 'For growing service companies',
    features: ['Unlimited workers', 'GPS time tracking', 'Advanced job management', 'Full reports & analytics', 'Payroll export', 'Customer management', 'Priority support', '14-day free trial'],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For large organizations',
    features: ['Everything in Professional', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee', 'On-premise option']
  }
];

export default function Signup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [formData, setFormData] = useState({
    company_name: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.admin_password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (selectedPlan === 'enterprise') {
      navigate('/contact');
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/signup`, {
        company_name: formData.company_name,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        plan: selectedPlan
      });

      login(res.data.token, res.data.user, res.data.company);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
      {/* Header */}
      <div className="border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-blue-300 transition-colors" data-testid="signup-logo">
            <Building2 className="w-6 h-6 text-blue-400" />
            <span className="font-bold text-lg">Hines Time</span>
          </Link>
          <Link to="/signin" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="signin-link">
            Already have an account? <span className="text-blue-400 font-medium">Sign in</span>
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {step === 1 ? (
          <>
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" data-testid="signup-title">Start Your Free Trial</h1>
              <p className="text-slate-400 text-base md:text-lg">Choose the plan that fits your team. 14-day free trial on all plans.</p>
            </div>

            {/* Plans */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-10" data-testid="plans-grid">
              {PLANS.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all ${
                    selectedPlan === plan.id
                      ? 'border-blue-500 bg-slate-800/80 shadow-lg shadow-blue-500/20'
                      : 'border-slate-700 bg-slate-800/40 hover:border-slate-500'
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs px-3">Most Popular</Badge>
                  )}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                    <p className="text-sm text-slate-400 mt-1">{plan.description}</p>
                  </div>
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-white">{plan.price}</span>
                    <span className="text-slate-400">{plan.period}</span>
                  </div>
                  <ul className="space-y-2">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Button
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-10"
                onClick={() => selectedPlan === 'enterprise' ? navigate('/contact') : setStep(2)}
                data-testid="continue-btn"
              >
                {selectedPlan === 'enterprise' ? 'Contact Sales' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        ) : (
          <div className="max-w-lg mx-auto">
            <Button variant="ghost" className="text-slate-400 mb-4" onClick={() => setStep(1)} data-testid="back-to-plans">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Plans
            </Button>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-xl text-white">Create Your Account</CardTitle>
                <p className="text-sm text-slate-400">
                  Starting with <span className="text-blue-400 font-medium capitalize">{selectedPlan}</span> plan — 14-day free trial
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Company Name</Label>
                    <Input
                      required
                      value={formData.company_name}
                      onChange={e => setFormData({ ...formData, company_name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Your Company Name"
                      data-testid="signup-company"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Your Full Name</Label>
                    <Input
                      required
                      value={formData.admin_name}
                      onChange={e => setFormData({ ...formData, admin_name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="John Smith"
                      data-testid="signup-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Email Address</Label>
                    <Input
                      type="email"
                      required
                      value={formData.admin_email}
                      onChange={e => setFormData({ ...formData, admin_email: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="john@company.com"
                      data-testid="signup-email"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Password</Label>
                    <Input
                      type="password"
                      required
                      value={formData.admin_password}
                      onChange={e => setFormData({ ...formData, admin_password: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Min 8 chars, upper, lower, number, special"
                      data-testid="signup-password"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Confirm Password</Label>
                    <Input
                      type="password"
                      required
                      value={formData.confirm_password}
                      onChange={e => setFormData({ ...formData, confirm_password: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Confirm your password"
                      data-testid="signup-confirm"
                    />
                  </div>

                  {error && <p className="text-sm text-red-400" data-testid="signup-error">{error}</p>}

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={submitting}
                    data-testid="signup-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {submitting ? 'Creating Account...' : 'Start Free Trial'}
                  </Button>

                  <p className="text-xs text-center text-slate-500">
                    By signing up you agree to our{' '}
                    <Link to="/terms" className="text-blue-400 hover:underline">Terms of Service</Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-blue-400 hover:underline">Privacy Policy</Link>
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
